import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { animeList } from "@/mocks/anime";
import { categories } from "@/mocks/categories";
import AnimeCard from "@/components/feature/AnimeCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import FilterBar, { type SortKey } from "./components/FilterBar";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const PAGE_SIZE = 8;

export default function Category() {
  const { type = "" } = useParams();
  const category = categories.find((c) => c.key === type);
  const categoryName = category?.name ?? decodeURIComponent(type);

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [status, setStatus] = useState("全部");
  const [sort, setSort] = useState<SortKey>("popularity");
  const [page, setPage] = useState(1);

  useDocumentTitle(
    `${categoryName} - 动漫全集 - 动漫网站`,
    `${categoryName}类动漫全集精选，${category?.desc ?? "汇聚丰富动漫资源"}尽在动漫网站。`
  );

  const filtered = useMemo(() => {
    const list = animeList.filter((a) => {
      const byCategory = a.category === categoryName || a.tags.includes(categoryName);
      const byRegion = region === "全部" || a.region === region;
      const byYear = year === "全部" || a.year === year;
      const byStatus = status === "全部" || a.status === status;
      return byCategory && byRegion && byYear && byStatus;
    });
    return list.sort((a, b) =>
      sort === "popularity"
        ? b.popularity - a.popularity
        : new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [categoryName, region, year, status, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setStatus("全部");
    setSort("popularity");
    setPage(1);
  };

  const changeFilter = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb items={[{ label: "动漫分类", to: "/category/hot-blood" }, { label: categoryName }]} />

      <header className="mt-5 mb-6">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          {categoryName}动漫全集
        </h1>
        <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
          {category?.desc ?? `收录${categoryName}题材的精选动漫作品，支持按地区、年份、更新状态筛选。`}
        </p>
      </header>

      <FilterBar
        region={region}
        year={year}
        status={status}
        sort={sort}
        onRegionChange={changeFilter(setRegion)}
        onYearChange={changeFilter(setYear)}
        onStatusChange={changeFilter(setStatus)}
        onSortChange={(s) => {
          setSort(s);
          setPage(1);
        }}
      />

      <div className="flex items-center justify-between mt-6 mb-4">
        <p className="text-sm text-foreground-500">
          共找到 <strong className="text-primary-600">{filtered.length}</strong> 部作品
        </p>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {pageItems.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} />
            ))}
          </div>
          <Pagination page={current} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="py-20 text-center bg-background-100 rounded-lg border border-background-200/70">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-50 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-2xl text-foreground-400" />
          </span>
          <h3 className="font-heading font-semibold text-lg text-foreground-900 mt-4">
            没有找到符合条件的动漫
          </h3>
          <p className="text-sm text-foreground-500 mt-2">试着调整筛选条件，或重置后重新浏览</p>
          <button
            onClick={resetFilters}
            className="mt-5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            重置筛选条件
          </button>
        </div>
      )}

      <div className="mt-12">
        <h2 className="font-heading font-semibold text-foreground-950 mb-4">其他热门分类</h2>
        <div className="flex flex-wrap gap-2">
          {categories
            .filter((c) => c.key !== type)
            .map((c) => (
              <Link
                key={c.key}
                to={`/category/${c.key}`}
                className="px-3.5 py-2 rounded-md bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
              >
                {c.name}
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
}